import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Search, Star, StarOff } from "lucide-react";

/** Categories list + inline hero copy / featured toggle editor. */
export default function CmsCategories() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/food-categories", { params: { limit: 100, q: q || undefined, depth: 0 } });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleFeatured = async (slug, featured) => {
    try {
      await http.patch(`/food-categories/${encodeURIComponent(slug)}`, { featured });
      toast.success(featured ? "Featured" : "Unfeatured");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  if (selected) return <CategoryEditor slug={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="max-w-5xl" data-testid="cms-categories">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Categories</h1>
      <p className="mt-1 text-sm text-slate-500">Hero copy, landing tiles and feature toggles for the top-level cuisine hubs.</p>

      <div className="mt-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search categories…" className="pl-8" data-testid="cms-categories-search" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>{loading ? "Searching…" : "Search"}</Button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <div key={c.slug} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cms-category-${c.slug}`}>
            {c.banner_image && (
              <img src={c.banner_image} alt="" className="mb-3 h-24 w-full rounded-md object-cover" />
            )}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  {c.emoji && <span>{c.emoji}</span>} {c.name}
                </div>
                <div className="mt-0.5 truncate text-xs text-slate-500">/{c.slug}</div>
                {c.short_description && (
                  <p className="mt-2 line-clamp-2 text-xs text-slate-600">{c.short_description}</p>
                )}
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${c.featured ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {c.featured ? "featured" : "hidden"}
              </span>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => setSelected(c.slug)} data-testid={`cms-category-edit-${c.slug}`} className="h-7">Edit</Button>
              <Button size="sm" variant="outline" onClick={() => toggleFeatured(c.slug, !c.featured)} className="h-7">
                {c.featured ? <StarOff className="mr-1 h-3 w-3" /> : <Star className="mr-1 h-3 w-3" />} {c.featured ? "Unfeature" : "Feature"}
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && !loading && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No categories loaded.
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryEditor({ slug, onBack }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await http.get(`/food-categories/${encodeURIComponent(slug)}`);
        setDoc(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load");
        onBack();
      }
    })();
    // eslint-disable-next-line
  }, [slug]);

  if (!doc) return <div className="p-8 text-slate-500">Loading category…</div>;

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/food-categories/${encodeURIComponent(slug)}`, {
        name: doc.name,
        short_description: doc.short_description,
        description: doc.description,
        banner_image: doc.banner_image,
        emoji: doc.emoji,
        featured: !!doc.featured,
        status: doc.status || "active",
      });
      toast.success("Category saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="cms-category-editor">
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to categories
      </button>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <Input value={doc.name || ""} onChange={(e) => set("name", e.target.value)} className="text-2xl font-semibold" data-testid="cms-category-name" />
          <div className="mt-1 text-xs text-slate-500">/food-pulse/categories/{doc.slug}</div>
        </div>
        <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cms-category-save">
          <Save className="mr-1 h-4 w-4" /> {busy ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-5">
        <div>
          <Label className="mb-1 block text-xs text-slate-600">Emoji</Label>
          <Input value={doc.emoji || ""} onChange={(e) => set("emoji", e.target.value)} className="w-24" />
        </div>
        <div>
          <Label className="mb-1 block text-xs text-slate-600">Hero banner image URL</Label>
          <Input value={doc.banner_image || ""} onChange={(e) => set("banner_image", e.target.value)} data-testid="cms-category-banner" />
        </div>
        <div>
          <Label className="mb-1 block text-xs text-slate-600">Short description (tagline)</Label>
          <Input value={doc.short_description || ""} onChange={(e) => set("short_description", e.target.value)} />
        </div>
        <div>
          <Label className="mb-1 block text-xs text-slate-600">Long description</Label>
          <Textarea rows={5} value={doc.description || ""} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={!!doc.featured} onCheckedChange={(v) => set("featured", !!v)} data-testid="cms-category-featured" />
          <span className="text-xs text-slate-600">Featured on cuisine-hub grid</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={doc.status || "active"} onChange={(e) => set("status", e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm">
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
          <span className="text-xs text-slate-500">Publish status</span>
        </div>
      </div>
    </div>
  );
}
